import { useSelector } from 'react-redux';
import {
  useAddRateMutation,
  useCreateRateScheduleMutation,
  useGetRatesQuery,
  useGetTSPsQuery,
  useGetZonesQuery,
  useRemoveRateMutation,
  useUpdateRatesMutation,
} from '../services/edgeApi';
import React, { useEffect, useMemo, useState } from 'react';
import { selectSortedTsps } from '../map/slice/mapSelectors';
import { LoadingIndicator } from '../helpers/LoadingIndicator';
import toast from 'react-hot-toast';
import { RatesTable, RatesTableRef } from './RatesTable';
import {
  CheckCircleIcon,
  CloseCircleIcon,
  CloseIcon,
  PlusIcon,
} from '@iconicicons/react';
import { PointRates, PointRatesRef } from './PointRates';
import {
  RateScheduleCategory,
  RateScheduleService,
  RatesPatch,
} from '@/types/api/RateSchedule';
import { Rate } from '@/types';
import { useLocation } from 'react-router-dom';
import { useBelowMap } from '../layouts/BelowMapContext';
import { ReusableModal } from '../modals/ReusableModal';
import { RateScheduleForm } from './RateScheduleForm';
import { isValidNumber } from '../utils/numberUtils';

export const Rates: React.FC = () => {
  const belowMap = useBelowMap();
  const { isLoading: isLoadingTsps } = useGetTSPsQuery(undefined);

  const [createRateScheduleTrigger] = useCreateRateScheduleMutation();
  const [updateRatesTrigger, updateInfo] = useUpdateRatesMutation();
  const [addRateTrigger] = useAddRateMutation();
  const [removeRateTrigger] = useRemoveRateMutation();

  const [selectedTSP, setSelectedTSP] = useState<string>('');
  const [rateSchedule, setRateSchedule] = useState<string>('');
  const [isDirty, setIsDirty] = useState<boolean>(false);
  const [isTouched, setIsTouched] = useState<boolean>(false);
  // const [activeTab, setActiveTab] = useState<'zone' | 'point'>('zone');
  const [isCreatingRateSchedule, setIsCreatingRateSchedule] =
    useState<boolean>(false);

  const zoneTableRef = React.useRef<RatesTableRef>(null);
  const pointTableRef = React.useRef<PointRatesRef>(null);

  const location = useLocation();
  const navigationRate = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const tsp = params.get('rate_tsp');
    const rateSchedule = params.get('rate_schedule');
    const createRateSchedule = Boolean(params.get('new'));
    const category = params.get('category') as RateScheduleCategory | null;
    const service = params.get('service') as RateScheduleService | null;
    return { tsp, rateSchedule, createRateSchedule, category, service };
  }, [location.search]);

  const { data: zones, isFetching: isLoadingZones } = useGetZonesQuery(
    { tsp: selectedTSP },
    { skip: selectedTSP === '' },
  );
  const {
    data: rateSchedules,
    isFetching: isFetchingRateSchedules,
    isLoading: isLoadingRateSchedules,
  } = useGetRatesQuery({ tsp: selectedTSP }, { skip: selectedTSP === '' });
  const tsps = useSelector(selectSortedTsps);

  const rates = useMemo(() => {
    if (!rateSchedules || !rateSchedule) {
      return [];
    }
    return (
      rateSchedules.find((rs) => rs.id.toString() === rateSchedule)?.rates || []
    );
  }, [rateSchedules, rateSchedule]);

  useEffect(() => {
    if (
      navigationRate.tsp ||
      navigationRate.rateSchedule ||
      navigationRate.createRateSchedule
    ) {
      belowMap.setActiveTab('rates-table');
    }
  }, []);

  useEffect(() => {
    if (!isLoadingTsps) {
      if (navigationRate.tsp) {
        const tsp = tsps.find((t) => t.id.toString() === navigationRate.tsp);
        if (tsp) {
          setSelectedTSP(tsp.id.toString());
        }
      }
      if (navigationRate.createRateSchedule) {
        setIsCreatingRateSchedule(true);
      }
    }
  }, [isLoadingTsps, navigationRate]);

  useEffect(() => {
    if (
      !navigationRate.rateSchedule ||
      selectedTSP !== navigationRate.tsp ||
      rateSchedules?.every(
        (rs) => rs.id.toString() !== navigationRate.rateSchedule,
      )
    ) {
      return;
    }
    setRateSchedule(navigationRate.rateSchedule);
  }, [isLoadingRateSchedules, navigationRate]);

  useEffect(() => {
    setIsTouched(false);
    setIsDirty(false);
  }, [selectedTSP, rateSchedule]);

  const createRate = async (rate: Partial<Rate>) => {
    if (!rateSchedule) return;
    try {
      await addRateTrigger({
        rate,
        rateScheduleId: Number(rateSchedule),
      }).unwrap();
      toast.success('Rate added successfully');
    } catch (error) {
      console.error('Error adding rate:', error);
      toast.error('Error adding rate');
    }
  };

  const deleteRate = async (rateId: number) => {
    if (!rateSchedule) return;
    try {
      await removeRateTrigger({
        rateId,
        rateScheduleId: Number(rateSchedule),
      }).unwrap();
      toast.success('Rate deleted successfully');
    } catch (error) {
      console.error('Error deleting rate:', error);
      toast.error('Error deleting rate');
    }
  };

  const saveRates = async () => {
    const defaultPatch: RatesPatch = {
      rate_schedule_id: Number(rateSchedule),
      rates: {
        update: [],
        create: [],
      },
    };
    const zonesPatch = zoneTableRef.current?.getPatch() ?? defaultPatch;
    const pointPatch = pointTableRef.current?.getPatch() ?? defaultPatch;
    if (zonesPatch.rate_schedule_id !== pointPatch.rate_schedule_id) {
      return;
    }
    const patch = {
      rate_schedule_id: Number(rateSchedule),
      rates: {
        update: [...zonesPatch.rates.update, ...pointPatch.rates.update],
        create: [...zonesPatch.rates.create, ...pointPatch.rates.create],
      },
    };
    try {
      await updateRatesTrigger(patch);
      toast.success('Rates updated successfully');
      setIsDirty(false);
    } catch (error) {
      console.error('Error updating rates:', error);
      toast.error('Error updating rates');
    }
  };

  const saveStatusMessage = useMemo(() => {
    if (!isTouched) {
      return null;
    }
    if (isDirty) {
      return (
        <span className="text-danger d-flex gap-2">
          <CloseCircleIcon />
          Unsaved changes
        </span>
      );
    }
    return (
      <span className="text-success d-flex gap-2">
        <CheckCircleIcon />
        All changes have been saved
      </span>
    );
  }, [isTouched, isDirty]);

  return (
    <>
      <div className="d-flex flex-column gap-2 m-3">
        <div className="input-group" style={{ maxWidth: '400px' }}>
          <label className="input-group-text">Pipeline</label>
          <select
            className="form-select"
            value={selectedTSP}
            onChange={(e) => setSelectedTSP(e.target.value)}
          >
            <option value=""></option>
            {tsps.map((tsp) => (
              <option key={tsp.id} value={tsp.id}>
                {tsp.name}
              </option>
            ))}
          </select>
        </div>
        <LoadingIndicator
          isLoading={isLoadingZones || isFetchingRateSchedules}
          className="my-4"
        />
        {!(isFetchingRateSchedules || isLoadingZones) && (
          <>
            {selectedTSP ? (
              <div className="d-flex gap-2">
                <div className="input-group" style={{ maxWidth: '400px' }}>
                  <label className="input-group-text">Rate Schedule</label>
                  <select
                    className="form-select"
                    value={rateSchedule}
                    onChange={(e) => setRateSchedule(e.target.value)}
                  >
                    <option value=""></option>
                    {rateSchedules?.map((rs) => (
                      <option key={rs.id} value={rs.id.toString()}>
                        {rs.name}
                      </option>
                    ))}
                  </select>
                </div>
                <button
                  className="btn btn-success"
                  onClick={() => setIsCreatingRateSchedule(true)}
                >
                  <PlusIcon />
                </button>
              </div>
            ) : (
              <div className="text-center alert alert-secondary my-4">
                Select a pipeline
              </div>
            )}
            {selectedTSP && (
              <>
                {rateSchedule ? (
                  <>
                    <div
                      className="d-flex justify-content-start align-items-center gap-2"
                      style={{
                        maxWidth: '800px',
                      }}
                    >
                      <button
                        className="btn btn-primary"
                        style={{
                          width: '400px',
                          cursor: updateInfo.isLoading ? 'wait' : 'pointer',
                        }}
                        onClick={saveRates}
                        disabled={updateInfo.isLoading}
                      >
                        Save
                      </button>
                      {saveStatusMessage}
                    </div>
                    <div className="my-3 d-flex flex-column gap-4">
                      <div className="d-flex justify-content-center">
                        <RatesTable
                          rateSchedule={rateSchedule}
                          rates={rates}
                          zones={zones}
                          ref={zoneTableRef}
                          onChange={() => {
                            setIsDirty(true);
                            setIsTouched(true);
                          }}
                        />
                      </div>
                      <PointRates
                        ref={pointTableRef}
                        rates={rates}
                        rateSchedule={rateSchedule}
                        tspId={selectedTSP}
                        onChange={() => {
                          setIsDirty(true);
                          setIsTouched(true);
                        }}
                        onCreate={createRate}
                        onDelete={deleteRate}
                      />
                    </div>
                  </>
                ) : (
                  <div className="text-center alert alert-secondary my-4">
                    Select a rate schedule
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>
      <ReusableModal
        size="lg"
        isOpen={isCreatingRateSchedule}
        onClose={() => setIsCreatingRateSchedule(false)}
      >
        <div className="mb-4">
          <h4 className="modal-title text-center align-items-center">
            New Rate Schedule
            <span role="button" className="float-end">
              <CloseIcon onClick={() => setIsCreatingRateSchedule(false)} />
            </span>
          </h4>
        </div>
        <RateScheduleForm
          defaultValues={{
            tsp_id: isValidNumber(selectedTSP)
              ? Number(selectedTSP)
              : undefined,
            service: navigationRate.service ?? undefined,
            category: navigationRate.category ?? undefined,
          }}
          onSubmit={async (newRateSchedule) => {
            try {
              const created = await createRateScheduleTrigger(
                newRateSchedule,
              ).unwrap();
              toast.success('Rate Schedule created successfully.');
              setIsCreatingRateSchedule(false);
              setSelectedTSP(created.tsp.id.toString());
              setRateSchedule(created.id.toString());
            } catch (error) {
              console.error('Error creating Rate Schedule:', error);
              toast.error('Error creating Rate Schedule.');
            }
          }}
        />
      </ReusableModal>
    </>
  );
};
